<template>
  <BaseModal 
    :show
    size="xl"
    :title="hasSubscription ? t('app.sysadmin.modal.manage_title') : t('app.sysadmin.modal.assign_title')"
    :show-actions="!!selectedPlanId"
    :loading="loading"
    :save-text="hasSubscription ? t('app.sysadmin.modal.change_plan_button') : t('app.sysadmin.modal.assign_plan_button')"
    save-class="bg-indigo-600 hover:bg-indigo-700"
    @close="$emit('close')"
    @save="handleSaveSubscription"
  >
    <div>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {{ restaurant.name }} ({{ restaurant.subdomain }})
      </p>

      <!-- Content -->
      <div class="bg-white dark:bg-gray-800 px-6 py-4 max-h-[70vh] overflow-y-auto">
        <!-- Admin Users Section -->
        <AdminUsersSection
          :loading-admins="loadingAdmins"
          :admins="admins"
          :show-create-admin-form="showCreateAdminForm"
          :creating-admin="creatingAdmin"
          :admin-form="adminForm"
          :has-admins="hasAdmins"
          @create-admin="handleCreateAdminAction"
          @cancel-create-admin="resetAdminForm"
        />

        <!-- Current Subscription Info (if exists) -->
        <CurrentSubscriptionInfo
          :has-subscription="hasSubscription"
          :subscription-details="subscriptionDetails"
          :restaurant="restaurant"
          :get-status-class="getStatusClass"
        />

        <!-- Plan Selection -->
        <PlanSelection
          :has-subscription="hasSubscription"
          :plans="plans"
          :selected-plan-id="selectedPlanId"
          :selected-plan="selectedPlan"
          :billing-cycle="billingCycle"
          @update:selected-plan-id="handleUpdateSelectedPlanId"
          @update:billing-cycle="handleUpdateBillingCycle"
        />

        <!-- Cancel Subscription (if exists) -->
        <CancelSubscriptionSection
          :has-subscription="hasSubscription"
          :restaurant="restaurant"
          :loading="loading"
          @cancel-subscription="handleCancelSubscriptionAction"
        />
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseModal from '@/components/common/BaseModal.vue';
import { useManageSubscriptionModal } from '@/composables/useManageSubscriptionModal';
import AdminUsersSection from '@/components/admin/AdminUsersSection.vue';
import CurrentSubscriptionInfo from '@/components/admin/CurrentSubscriptionInfo.vue';
import PlanSelection from '@/components/admin/PlanSelection.vue';
import CancelSubscriptionSection from '@/components/admin/CancelSubscriptionSection.vue';

const { t } = useI18n();

const props = defineProps<{
  show: boolean;
  restaurant: any;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'updated'): void;
}>();

// Use composable for business logic
const restaurantRef = computed(() => props.restaurant);
const {
  loading,
  plans,
  selectedPlanId,
  billingCycle,
  subscriptionDetails,
  loadingAdmins,
  admins,
  showCreateAdminForm,
  creatingAdmin,
  adminForm,
  hasSubscription,
  selectedPlan,
  hasAdmins,
  handleCreateAdmin,
  resetAdminForm,
  saveSubscription,
  cancelSubscription,
  getStatusClass,
  initializeModal
} = useManageSubscriptionModal(restaurantRef);

// Helper genérico para acciones que emiten 'updated' al tener éxito
const handleActionWithUpdate = async (action: () => Promise<boolean | undefined>) => {
  const success = await action();
  if (success) {
    emit('updated');
  }
};

// Methods
const handleSaveSubscription = () => handleActionWithUpdate(saveSubscription);
const handleCancelSubscriptionAction = (immediate: boolean) => 
  handleActionWithUpdate(() => cancelSubscription(immediate));
const handleCreateAdminAction = async () => {
  await handleCreateAdmin();
};

const handleUpdateSelectedPlanId = (value: number | null) => {
  selectedPlanId.value = value;
};

const handleUpdateBillingCycle = (value: 'monthly' | 'annual') => {
  billingCycle.value = value;
};

// Lifecycle
onMounted(async () => {
  await initializeModal();
});
</script>
